import { ChangeEvent } from 'react';

import { Dayjs } from 'dayjs';
import { ProjectResponse, WorkPlaceResponse } from 'generated/index';
import { Control, Controller, UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Event } from 'components/roster-portal/activity-planning/types';

import { GRAY } from 'constants/colors';
import { WEEKDAYS } from 'constants/date';

import { shouldDisableDateInRange } from 'utils/date';

import {
  Box,
  Checkbox,
  FormControlLabel,
  FormLabel,
  InputAdornment,
  StepLabel
} from '@mui/material';

import Grid from '@mui/material/Grid';

import {
  ControlledCheckbox,
  ControlledDatePicker,
  ControlledSelect,
  ControlledTextField,
  ControlledTimePicker
} from 'components/shared/ControlledFields';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';

import { FieldKey } from '../input-field-grants';
import {
  GridContainer,
  GridCompact3,
  GridCompact4,
  GridCompact6
} from 'components/shared/Grid/Grid.components';

type Props = {
  enable?: boolean;
  control: Control<any>;
  event?: Event;
  minuteDuration?: number;
  hourDuration?: number;
  periodType?: 'WEEK' | 'DAY' | undefined;
  dayInWeekSet: string[];
  selectedProject?: ProjectResponse | null;
  selectedWorkPlace?: WorkPlaceResponse | null;
  hasFieldGrant: (key: FieldKey) => boolean;
  setValue: UseFormSetValue<any>;
};

const WhenSection = ({
  enable,
  control,
  event,
  minuteDuration,
  hourDuration,
  periodType,
  dayInWeekSet,
  selectedProject,
  selectedWorkPlace,
  hasFieldGrant,
  setValue
}: Props) => {
  const { t } = useTranslation();

  if (!enable) return null;

  return (
    <MultiStepForm.Section
      title="Wanneer"
      validateFields={[
        'duration',
        'startDate',
        'startTime',
        ...(hasFieldGrant('START_DATE_VALIDATION') ? ['startDateIsValid'] : []),
        ...(hasFieldGrant('DAY_IN_WEEK_SET') ? ['dayInWeekSet'] : [])
      ]}
    >
      <GridContainer>
        <GridCompact6>
          <ControlledDatePicker
            name="startDate"
            label={t('startDate')}
            control={control}
            variant="outlined"
            fullWidth
            shouldDisableDate={(date: Dayjs) => {
              const { validFrom, validTo } = selectedProject ||
                selectedWorkPlace || {
                  validFrom: null,
                  validTo: null
                };
              return shouldDisableDateInRange(
                date,
                new Date(validFrom || NaN),
                new Date(validTo || NaN)
              );
            }}
          />
        </GridCompact6>
        <GridCompact6>
          {hasFieldGrant('START_TIME') && (
            <ControlledTimePicker
              name="startTime"
              label={t('startTime')}
              control={control}
              variant="outlined"
              fullWidth
            />
          )}
        </GridCompact6>
      </GridContainer>

      <GridContainer>
        <GridCompact3>
          <ControlledTextField
            name="hourDuration"
            label={t('hours')}
            control={control}
            variant="outlined"
            fullWidth
            slotProps={{
              input: {
                inputProps: {
                  inputMode: 'numeric',
                  pattern: '[0-9]*'
                }
              }
            }}
            onBlur={() => {
              if (!minuteDuration && hourDuration)
                setValue('minuteDuration', 0);
              if (!hourDuration) setValue('hourDuration', 0);
            }}
          />
        </GridCompact3>
        <GridCompact3>
          <ControlledTextField
            name="minuteDuration"
            label={t('minutes')}
            control={control}
            variant="outlined"
            fullWidth
            slotProps={{
              input: {
                inputProps: {
                  inputMode: 'numeric',
                  pattern: '[0-9]*'
                }
              }
            }}
            onBlur={() => {
              if (minuteDuration && !hourDuration) setValue('hourDuration', 0);
              if (!minuteDuration) setValue('minuteDuration', 0);
            }}
          />
        </GridCompact3>
        <GridCompact6>
          {hasFieldGrant('END_TIME') && (
            <ControlledTimePicker
              name="endTime"
              control={control}
              label={t('endTime')}
              variant="outlined"
              fullWidth
              disabled
            />
          )}
        </GridCompact6>
      </GridContainer>

      <Box>
        {hasFieldGrant('RECURRENCE_RULE_EDIT') && (
          <Box sx={{ marginBottom: '10px' }}>
            <ControlledCheckbox
              control={control}
              name="isRecurring"
              label={t('recurrenceRule')}
            />
          </Box>
        )}

        {hasFieldGrant('PERIOD_TYPE') && (
          <Box display="flex" gap={2.5}>
            <ControlledSelect
              control={control}
              name="periodType"
              label={t('repeatOn')}
              variant="outlined"
              options={[
                { label: t('timeUnits.DAY'), value: 'DAY' },
                { label: t('timeUnits.WEEK'), value: 'WEEK' }
              ]}
            />

            <ControlledTextField
              name="interval"
              label={t('interval')}
              control={control}
              variant="outlined"
              sx={{ width: '144px' }}
              slotProps={{
                input: {
                  inputProps: {
                    inputMode: 'numeric',
                    pattern: '[0-9]*'
                  },
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      disableTypography
                      sx={{ color: GRAY, fontSize: '12px' }}
                    >
                      {periodType === 'DAY' ? 'dagen' : 'weken'}
                    </InputAdornment>
                  )
                }
              }}
            />
          </Box>
        )}

        {hasFieldGrant('WEEK_DAYS') && (
          <>
            <FormLabel sx={{ marginTop: '24px' }}>{t('repeatOn')}</FormLabel>
            <Grid container flexDirection="column">
              {WEEKDAYS.map((d: string, i: number) => (
                <GridCompact4 key={i}>
                  <Controller
                    key={d}
                    name="dayInWeekSet"
                    control={control}
                    render={({ field }) => {
                      const value: string = d.toUpperCase();
                      return (
                        <FormControlLabel
                          {...field}
                          label={t(`weekday.${d}`)}
                          control={
                            <Checkbox
                              value={value}
                              disabled={periodType !== 'WEEK'}
                              checked={(dayInWeekSet || [])?.includes(value)}
                              onChange={(
                                event: ChangeEvent<HTMLInputElement>,
                                checked: boolean
                              ) => {
                                const v: string =
                                  event.target.value.toUpperCase();
                                field.onChange(
                                  checked
                                    ? [...field.value, v]
                                    : field.value.filter(
                                        (value: string) => value !== v
                                      )
                                );
                              }}
                            />
                          }
                        />
                      );
                    }}
                  />
                </GridCompact4>
              ))}

              <GridCompact4>
                <Controller
                  name="dayInWeekSet"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      label={t('everyDay')}
                      control={
                        <Checkbox
                          {...field}
                          checked={dayInWeekSet.length > 0}
                          disabled={periodType !== 'WEEK'}
                          indeterminate={
                            dayInWeekSet.length > 0 &&
                            dayInWeekSet.length < WEEKDAYS.length
                          }
                          onChange={(_, checked) =>
                            setValue(
                              'dayInWeekSet',
                              checked
                                ? WEEKDAYS.map((d: string) => d.toUpperCase())
                                : []
                            )
                          }
                        />
                      }
                    />
                  )}
                />
              </GridCompact4>
            </Grid>
          </>
        )}
      </Box>

      {hasFieldGrant('RECURRENCE_RULE_VIEW') && (
        <Box display="flex" gap={2.5}>
          <StepLabel>{t('recurrenceRule')}</StepLabel>
          <Box>{event?.recurrenceRuleRepresentation}</Box>
        </Box>
      )}
    </MultiStepForm.Section>
  );
};

export default WhenSection;
